/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import sizes from 'constants/sizes';
import menuTypes from 'constants/menuTypes';
import employeesSource from 'constants/employeesSource';

import { useTranslate } from 'hooks/useTranslate';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';

import transformData from 'modules/_shared/components/Tables/helpers';
import AddEmployeesToPlan from 'modules/_shared/components/Modals/AddEmployeesToPlan';
import SyncEmployeesModal from 'modules/beneficiaries/modals/SyncEmployees';
import {
  getEmployeesInfo,
  getSocietyEmployees,
  getSocietyEmployeesPaginated,
  saveEmployees,
} from 'modules/beneficiaries/redux/employeeActions';
import { setModal } from 'modules/_shared/redux/modalActions';
import { getStockPlans } from 'modules/beneficiaries/redux/planActions';
import { parseEmployeesData } from 'modules/beneficiaries/utils/parseEmployeesData';
import { getEmployeesPageDescription } from 'modules/beneficiaries/utils/getEmployeesPageDescription';
import { useGetPlanSectionPermissions } from 'modules/beneficiaries/hooks/useGetPlanSectionPermissions';
import isSuperAdmin from 'utils/isSuperAdmin';
import { COLUMN_FIELD_TO_SORT } from '../constants/sortFieldMap';

import tableColumns from '../utils/tableColumns';

const isEnabledAndValidated = (integration) =>
  integration.enabled && integration.validated;

const INITIAL_PAGE = 1;
const INITIAL_ITEMS = 10;
const DEFAULT_SORT_BY = 'externalId';
const DEFAULT_SORT_ORDER = 'asc';

export const useEmployeeList = () => {
  const { t, i18n } = useTranslate();
  const dispatch = useDispatch();
  const { societyId } = useParams();

  const { isAdmin, isReadOnly } = useGetPlanSectionPermissions().data;

  const user = useSelector((state) => state.user);
  const actualSociety = useSelector((state) => state.society?.actualSociety);
  const plans = useSelector((state) => state.plans);

  const isSuperAdminUser = isSuperAdmin(user);

  const [isLoading, setIsLoading] = useState(true);
  const isFetchingRef = useRef(false);
  const [tableData, setTableData] = useState([]);
  const [counts, setCounts] = useState(null);
  const [pagination, setPagination] = useState({
    page: INITIAL_PAGE,
    items: INITIAL_ITEMS,
    totalRows: 0,
    totalPages: 0,
  });
  const [kpiFilter, setKpiFilter] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);

  useGetMenu(menuTypes.EXTENDED);
  useGetActualSociety(societyId);

  const beneficiariesExternalIdList = useMemo(() => {
    const ids =
      actualSociety?.beneficiaries
        ?.map((b) => b?.employeeExternalId ?? null)
        ?.filter(Boolean) ?? [];
    return [...new Set(ids)];
  }, [actualSociety]);

  const statusFilter = useMemo(() => {
    if (kpiFilter === 'active') return 'active';
    if (kpiFilter === 'activeWithIncentives') return 'activeWithIncentives';
    if (kpiFilter === 'inactiveWithIncentives') return 'inactiveWithIncentives';
    return '';
  }, [kpiFilter]);

  const fetchEmployeesPaginated = useCallback(async () => {
    if (!societyId) {
      setIsLoading(false);
      return;
    }
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      const result = await dispatch(
        getSocietyEmployeesPaginated(societyId, {
          page: pagination.page,
          items: pagination.items,
          sortBy,
          sortOrder,
          searchTerm: searchTerm.trim() || undefined,
          statusFilter: statusFilter || undefined,
        })
      );

      if (!result?.data) return;

      const newData = parseEmployeesData({
        employees: result.data,
        beneficiariesExternalId: beneficiariesExternalIdList,
        society: actualSociety,
        t,
      });

      setTableData(
        newData?.length > 0
          ? transformData({
              data: newData,
              columns: tableColumns(i18n),
            })
          : []
      );
      setCounts(result.counts || null);
      setPagination((prev) => ({
        ...prev,
        totalRows: result.pagination?.totalRows ?? 0,
        totalPages: result.pagination?.totalPages ?? 0,
      }));
    } catch (error) {
      console.error('fetchEmployeesPaginated', error); // eslint-disable-line no-console
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, [
    societyId,
    pagination.page,
    pagination.items,
    sortBy,
    sortOrder,
    searchTerm,
    statusFilter,
    beneficiariesExternalIdList,
    actualSociety,
    dispatch,
  ]);

  useEffect(() => {
    if (societyId) {
      dispatch(getStockPlans(societyId));
    }
  }, [societyId, dispatch]);

  useEffect(() => {
    if (actualSociety) {
      fetchEmployeesPaginated();
    }
  }, [fetchEmployeesPaginated, actualSociety]);

  const currentTableColumns = useMemo(
    () => tableColumns(i18n),
    [i18n.language]
  );

  const pageDescription = useMemo(
    () =>
      getEmployeesPageDescription({
        t,
        lastImportationDate: counts?.lastImportationDate,
        hasFactorialIntegration:
          actualSociety?.integrations?.factorial?.enabled ?? false,
      }),
    [actualSociety, counts, i18n.language]
  );

  const hasFactorialIntegration =
    actualSociety?.integrations?.factorial?.enabled === true;

  const activeEmployeesWithIncentive = counts?.totalActiveWithIncentive ?? 0;
  const inactiveEmployeesWithIncentive =
    counts?.totalInactiveWithIncentive ?? 0;
  const activeEmployeesCount = counts?.totalActive ?? 0;
  const totalEmployeesCount = counts?.total ?? 0;

  const handleKpiFilterClick = useCallback((filter) => {
    setKpiFilter((prev) => (prev === filter ? null : filter));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleSyncEmployees = useCallback(
    (onSyncComplete) => {
      dispatch(
        setModal(
          <SyncEmployeesModal
            onSyncComplete={onSyncComplete ?? fetchEmployeesPaginated}
            size={sizes.XL}
          />
        )
      );
    },
    [dispatch, fetchEmployeesPaginated]
  );

  const handleFilterEmployees = useCallback(() => {
    if (isReadOnly) return;
    if (plans?.length) {
      dispatch(setModal(<AddEmployeesToPlan size={sizes.LG} />));
    } else {
      return Swal.fire({
        icon: 'warning',
        title: `<h4 class="nk-modal-title">${t('HasNoPlans')}</h4>`,
        html: `<p class="text-soft px-4">${t('HasNoPlansDescription')}</p>
        <p class="text-primary" style="margin-top:24px;margin-bottom:30px">
          ${t('PlansListPage')}
        </p>`,
        confirmButtonText: t('OK'),
        allowOutsideClick: false,
        showCancelButton: false,
        confirmButtonColor: '#6576FF',
      });
    }
  }, [dispatch, isReadOnly, plans?.length, t]);

  const handleLoadEmployees = useCallback(async () => {
    try {
      if (!isSuperAdminUser) return;

      setIsLoading(true);

      const integrations = {};
      Object.entries(actualSociety?.integrations)?.forEach(([key, value]) => {
        if (isEnabledAndValidated(value) && value.token) {
          integrations[key] = value.token;
        }
      });

      const hasValidKeys = Object.keys(integrations).length > 0;

      if (hasValidKeys) {
        let employeesList = [];

        if (integrations.factorial) {
          const currentEmployees =
            (await dispatch(getSocietyEmployees(societyId))) || [];
          employeesList = await getEmployeesInfo(
            employeesSource.FACTORIAL,
            Array.isArray(currentEmployees) ? currentEmployees : [],
            actualSociety
          );
        }

        setIsLoading(false);

        if (employeesList.newEmployees || employeesList.updatedEmployees) {
          return Swal.fire({
            icon: 'info',
            title: `<h4 class="nk-modal-title">${t(
              'EmployeesImportModalTitle'
            )}</h4>`,
            html: `<p class="text-soft px-4" style="margin-top:24px;margin-bottom:30px">
            ${t('EmployeesImportModalDescription', {
              newEmployees: employeesList.newEmployees?.length,
              updatedEmployees: employeesList.updatedEmployees?.length,
            })}
          </p>`,
            confirmButtonText: t('EmployeesImportModalConfirmButtonText'),
            confirmButtonColor: '#6576ff',
            allowOutsideClick: false,
            showCancelButton: true,
          }).then((result) => {
            if (result.isConfirmed) {
              dispatch(
                saveEmployees(
                  employeesList,
                  employeesSource.FACTORIAL,
                  actualSociety['_id']
                )
              );
            }
          });
        }
      } else {
        return Swal.fire({
          icon: 'warning',
          title: `<h4 class="nk-modal-title">${t('HasNotValidKeys')}<br></h4>`,
          html: `<p class="text-soft px-4">${t(
            'HasNotValidKeysDescription'
          )}</p>
        <p class="text-primary" style="margin-top:24px;margin-bottom:30px">
          ${t('IntegrationsTabAndPage')}
        </p>`,
          confirmButtonText: t('OK'),
          allowOutsideClick: false,
          showCancelButton: false,
          confirmButtonColor: '#6576FF',
        });
      }
    } catch (error) {
      console.error('handleLoadEmployees', error); // eslint-disable-line no-console
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, isSuperAdminUser, actualSociety, societyId, t]);

  const handleChangePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleChangeRowsPerPage = useCallback((newItems) => {
    setPagination((prev) => ({
      ...prev,
      items: newItems,
      page: 1,
    }));
  }, []);

  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleSort = useCallback((column, direction) => {
    const sortField = COLUMN_FIELD_TO_SORT[column?.field] ?? DEFAULT_SORT_BY;
    setSortBy(sortField);
    setSortOrder(direction === 'asc' ? 'asc' : 'desc');
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  return {
    data: {
      actualSociety,
      tableData,
      currentTableColumns,
      kpiFilter,
      isAdmin,
      isReadOnly,
      isSuperAdmin: isSuperAdminUser,
      hasFactorialIntegration,
      pageDescription,
      activeEmployeesWithIncentive,
      inactiveEmployeesWithIncentive,
      activeEmployeesCount,
      totalEmployeesCount,
      pagination,
    },
    actions: {
      handleLoadEmployees,
      handleFilterEmployees,
      handleSyncEmployees,
      handleKpiFilterClick,
      handleChangePage,
      handleChangeRowsPerPage,
      handleSearchChange,
      handleSort,
      fetchEmployeesPaginated,
    },
    isLoading,
    isError: false,
  };
};
